import { z } from 'zod';
import { FilterGroupSchema, SortSchema } from './filters';
import { DEFAULT_EXPORT_OPTIONS, EXPORT_TRANSFORMS, type ExportColumn, type ExportFormat } from './models';

/**
 * Export requests.
 *
 * An export is a snapshot: the scope (which rows) plus the columns (which values, and how they
 * are formatted) are stored with the export, so a downloaded file can always be explained later.
 */
export const ExportScopeSchema = z.object({
  projectId: z.string().max(64).optional(),
  filter: FilterGroupSchema.optional(),
  search: z.string().trim().max(200).optional(),
  sort: z.array(SortSchema).max(3).optional(),
  /** Explicit row ids win over the filter — this is "export what I selected". */
  ids: z.array(z.string().max(64)).max(10_000).optional(),
  status: z.enum(['active', 'discarded', 'all']).default('active'),
  sources: z.array(z.string().max(40)).max(10).optional(),
  tags: z.array(z.string().max(60)).max(20).optional(),
});

export const ExportColumnSchema = z.object({
  field: z.string().min(1).max(120),
  header: z.string().trim().min(1).max(120),
  transform: z.enum(EXPORT_TRANSFORMS).optional(),
  defaultValue: z.string().max(200).optional(),
});

export const ExportOptionsSchema = z.object({
  /** UTF-8 BOM so Excel opens Arabic text correctly. */
  includeBom: z.boolean().default(true),
  delimiter: z.enum([',', ';', '\t']).default(','),
  listSeparator: z.string().min(1).max(5).default('; '),
});

/**
 * Where a Google Sheets export is written. `spreadsheet` accepts a pasted spreadsheet link or a
 * bare id — people have the link, not the id.
 */
export const SheetTargetSchema = z.object({
  spreadsheet: z.string().trim().min(1, 'Paste the link to your Google Sheet.').max(300),
  tabName: z.string().trim().min(1).max(80).default('Businesses'),
});

export const CreateExportSchema = z
  .object({
    format: z.enum(['csv', 'xlsx', 'google_sheets']),
    scope: ExportScopeSchema,
    columns: z.array(ExportColumnSchema).min(1, 'Choose at least one column.').max(120),
    options: ExportOptionsSchema.prefault({}),
    fileName: z.string().trim().max(120).optional(),
    sheet: SheetTargetSchema.optional(),
  })
  .superRefine((request, ctx) => {
    if (request.format === 'google_sheets' && !request.sheet) {
      ctx.addIssue({ code: 'custom', path: ['sheet'], message: 'Choose the Google Sheet to write into.' });
    }
  });
export type CreateExportRequest = z.input<typeof CreateExportSchema>;

export interface ExportSummary {
  id: string;
  format: ExportFormat;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'expired';
  fileName: string | null;
  fileSize: number | null;
  rowCount: number | null;
  errorMessage: string | null;
  createdAt: number;
  completedAt: number | null;
  expiresAt: number | null;
  /** Relative download path, present only while the file still exists. */
  downloadUrl: string | null;
  /** Link to the written spreadsheet, for Google Sheets exports. */
  sheetUrl: string | null;
}

/** Sensible starting columns when the user has not customised anything. */
export function defaultExportColumns(): ExportColumn[] {
  return [
    { field: 'name', header: 'Name' },
    { field: 'category', header: 'Category' },
    { field: 'phone', header: 'Phone', transform: 'phone_e164' },
    { field: 'email', header: 'Email' },
    { field: 'website', header: 'Website' },
    { field: 'address', header: 'Address' },
    { field: 'area', header: 'District' },
    { field: 'city', header: 'City' },
    { field: 'country', header: 'Country', transform: 'country_name' },
    { field: 'rating', header: 'Rating' },
    { field: 'reviewCount', header: 'Reviews' },
    { field: 'linkedinUrl', header: 'LinkedIn' },
    { field: 'sources', header: 'Sources', transform: 'join_comma' },
  ];
}

export { DEFAULT_EXPORT_OPTIONS };
